import { Controller } from "@hotwired/stimulus"

const IDLE_MS = 6000
const KEYSTROKE_MS = 70
const HOLD_MS = 1000
const DWELL_MS = 9000
const LOOPING_CLASS = 'hero-search-looping'

// Connects to data-controller="public--hero-loop"
//
// The hero's self-typing loop. Once the visitor has been idle with the field
// in view, it types each pinned market's name into the ghost span over the
// field — never the real input, so no keyboard opens on mobile and no
// typeahead runs — swaps the snapshot frame to that market, holds the name a
// moment, then empties the field to a blinking caret so it reads as hers to
// type in, and ends every pass on the visitor's own market. Any focus, pointer or key in the
// hero hands the field back for good. It never writes the role=status line;
// the search owns it.
//
// PostHog hook points (AI-175): `public--hero-loop:shown` ({uuid, name}) on
// every swap and `public--hero-loop:stopped` ({via}) when the visitor takes
// over mid-pass.
export default class extends Controller {
  static targets = ['field', 'ghost', 'snapshot']
  static values = { markets: Array, homeUuid: String, homeName: String }

  connect() {
    if (document.documentElement.hasAttribute('data-turbo-preview')) return
    if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return

    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting
      this.inView ? this.arm() : this.rest()
    }, { threshold: 1 })
    this.observer.observe(this.fieldTarget)
  }

  disconnect() {
    this.observer?.disconnect()
    this.rest()
  }

  visibilityChanged() {
    document.visibilityState === 'visible' ? this.arm() : this.rest()
  }

  // Waits out the idle time before a pass; nothing starts while one is pending.
  arm() {
    if (this.stopped || this.timer || !this.inView || document.visibilityState !== 'visible') return
    this.after(IDLE_MS, () => this.start())
  }

  // The visitor's own interaction: the loop ends for good, and a pass cut
  // short still ends on her market.
  stop(event) {
    if (this.stopped) return
    this.stopped = true
    if (this.rest()) this.dispatch('stopped', { detail: { via: event.type } })
  }

  // Clears the ghost and the timers, finishing an interrupted pass on the
  // visitor's market; reports whether a pass was underway.
  rest() {
    const interrupted = Boolean(this.pass)
    if (this.shown && this.shown.uuid !== this.homeUuidValue) this.swap(this.home)
    clearTimeout(this.timer)
    this.timer = null
    this.pass = null
    this.shown = null
    this.ghostTarget.textContent = ''
    this.ghostTarget.hidden = true
    this.fieldTarget.classList.remove(LOOPING_CLASS)
    return interrupted
  }

  // A pass is the pinned markets, then the visitor's own — once, at the end,
  // when it is one of them.
  start() {
    if (!this.idle) return
    this.pass = [...this.marketsValue.filter(market => market.uuid !== this.homeUuidValue), this.home]
    this.step(0)
  }

  step(index) {
    const market = this.pass[index]
    this.fieldTarget.classList.add(LOOPING_CLASS)
    this.ghostTarget.hidden = false
    this.type(market.name, '', () => {
      this.swap(market)
      this.shown = market
      this.dispatch('shown', { detail: market })
      window.posthog?.capture('www.hero_loop_market_shown', { name: market.name })
      this.after(HOLD_MS, () => {
        this.empty()
        this.after(DWELL_MS, () => this.step((index + 1) % this.pass.length))
      })
    })
  }

  // The searched name gives way to the empty field while its snapshot stays.
  // The ghost keeps its caret blinking over the placeholder — an invitation,
  // not a focus: nothing on the page loses its place — and the search
  // empties its typeahead on the event.
  empty() {
    this.ghostTarget.textContent = ''
    this.fieldTarget.classList.remove(LOOPING_CLASS)
    this.dispatch('emptied')
  }

  type(name, typed, done) {
    this.ghostTarget.textContent = typed
    if (typed === name) return done()
    this.after(KEYSTROKE_MS, () => this.type(name, name.slice(0, typed.length + 1), done))
  }

  swap(market) {
    this.dispatch('loading', { detail: market })
    const frame = this.snapshotTarget.querySelector('turbo-frame')
    frame.removeAttribute('src')
    frame.src = `/us/markets/${market.uuid}/hero`
  }

  after(ms, callback) {
    this.timer = setTimeout(() => {
      this.timer = null
      callback()
    }, ms)
  }

  // Idle: the search's input holds nothing of the visitor's own — empty or
  // still the pre-filled market — and is not focused.
  get idle() {
    const input = this.fieldTarget.querySelector('.aa-Input')
    return document.activeElement !== input && (input.value === '' || input.value === this.homeNameValue)
  }

  get home() {
    return { uuid: this.homeUuidValue, name: this.homeNameValue }
  }
}
